import { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Button, Popover } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { useFilters, useFiltersDispatch } from './Context/FiltersContext';
import TrackerFilterTextField from './TrackerFilterTextField';
import TrackerFilterStatusSelection from './TrackerFilterStatusSelection';
import TrackerFilterAutoCompleteSelection from './TrackerFilterAutoCompleteSelection';
import LaurelWreathIcon from './Icons/LaurelWreathIcon';
import CardsIcon from './Icons/CardsIcon';
import UsersIcon from './Icons/UsersIcon';
import {
  getTrackerFilterAutoCompleteOptionsGroupByFunc,
  getTrackerFilterAutoCompleteOptionsUseMemoFactoryFunc,
} from './trackerFilterHelpers';
import {
  DropdownTableAutocompleteOptionCampaign,
  DropdownTableAutocompleteOptionTeamMember,
  DropdownTableAutocompleteOptionType,
  DropdownTableContext,
} from '../../shared/contexts/DropdownTableContext';
import { TrackerDataType } from '../../shared/constants/constants';
import { TrackerFilterAutocompleteGroupByFunc, TrackerFilterStatus } from './types';

interface CalendarFilterPopoverProps {
  anchorEl: HTMLButtonElement | null;
  onClose: () => void;
  eventHandler: any;
  setFiltersOn: (value: boolean) => void;
}

const FILTER_INPUTS_GRID_COL_NUM = 18;

export default function CalendarFilterPopover(props: Readonly<CalendarFilterPopoverProps>) {
  const { anchorEl, onClose, eventHandler, setFiltersOn } = props;
  const open = Boolean(anchorEl);
  const filters = useFilters();
  const filtersDispatch = useFiltersDispatch();
  const dropdownTableContext = useContext(DropdownTableContext);

  const [serviceOrTopicFilter, setServiceOrTopicFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<TrackerFilterStatus[]>([]);
  const [campaignFilter, setCampaignFilter] = useState<DropdownTableAutocompleteOptionCampaign[]>([]);
  const [typeFilter, setTypeFilter] = useState<DropdownTableAutocompleteOptionType[]>([]);
  const [teamMemberFilter, setTeamMemberFilter] = useState<DropdownTableAutocompleteOptionTeamMember[]>([]);

  const statusFilterOptions = useMemo(
    () => getTrackerFilterAutoCompleteOptionsUseMemoFactoryFunc(dropdownTableContext, 'status'),
    [dropdownTableContext]
  );
  const campaignsFilterOptions = useMemo(
    () => getTrackerFilterAutoCompleteOptionsUseMemoFactoryFunc(dropdownTableContext, 'campaigns'),
    [dropdownTableContext]
  );
  const campaignsFilterGroupBy = getTrackerFilterAutoCompleteOptionsGroupByFunc('campaigns');
  const typesFilterOptions = useMemo(
    () => getTrackerFilterAutoCompleteOptionsUseMemoFactoryFunc(dropdownTableContext, 'type'),
    [dropdownTableContext]
  );
  const typesFilterGroupBy = getTrackerFilterAutoCompleteOptionsGroupByFunc('type');
  const teamMembersFilterOptions = useMemo(
    () => getTrackerFilterAutoCompleteOptionsUseMemoFactoryFunc(dropdownTableContext, 'lead'),
    [dropdownTableContext]
  );
  const teamMembersFilterGroupBy = getTrackerFilterAutoCompleteOptionsGroupByFunc('lead');

  useEffect(() => {
    if (!open) {
      return;
    }

    setServiceOrTopicFilter(filters.serviceOrTopic || filters.service || filters.topic || '');
    setStatusFilter(filters.status || []);
    setCampaignFilter(filters.Campaigns || []);
    setTypeFilter(filters.type || []);
    setTeamMemberFilter(filters['Team Members'] || []);
  }, [filters, open]);

  const handleApply = () => {
    const nextFilters: Record<string, any> = {};

    if (serviceOrTopicFilter.trim()) nextFilters.serviceOrTopic = serviceOrTopicFilter.trim();
    if (statusFilter.length > 0) nextFilters.status = statusFilter;
    if (campaignFilter.length > 0) nextFilters.Campaigns = campaignFilter;
    if (typeFilter.length > 0) nextFilters.type = typeFilter;
    if (teamMemberFilter.length > 0) nextFilters['Team Members'] = teamMemberFilter;

    filtersDispatch({
      type: 'setFilters',
      payload: nextFilters,
    });
    setFiltersOn(Object.keys(nextFilters).length > 0);
    onClose();
  };

  const getColor = () => {
    if (eventHandler.type === TrackerDataType.MEDIA_INTERACTION) {
      return 'mediaInteraction';
    }
    return 'primary';
  };

  return (
    <Popover
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{ vertical: -8, horizontal: 'left' }}
      slotProps={{
        paper: {
          sx: {
            minWidth: '700px',
            maxHeight: '75vh',
            overflowY: 'auto',
          },
        },
      }}
      data-cy="calendar-filter-popover"
    >
      <Box sx={{ width: '700px', padding: '27px 20px' }}>
        <Grid container spacing="10px" columns={FILTER_INPUTS_GRID_COL_NUM}>
          <TrackerFilterTextField
            label="SERVICE/TOPIC"
            value={serviceOrTopicFilter}
            onChange={(value: string) => setServiceOrTopicFilter(value)}
          />
          <TrackerFilterStatusSelection
            options={statusFilterOptions}
            selectedStatus={statusFilter}
            onSelect={(selectedStatus) => setStatusFilter(selectedStatus)}
          />
          <TrackerFilterAutoCompleteSelection
            label="CAMPAIGN"
            icon={<LaurelWreathIcon sx={{ fontSize: '20px' }} />}
            options={campaignsFilterOptions}
            groupBy={campaignsFilterGroupBy as TrackerFilterAutocompleteGroupByFunc}
            values={campaignFilter}
            onChange={(values) => setCampaignFilter(values as DropdownTableAutocompleteOptionCampaign[])}
            type={eventHandler.type}
          />
          <TrackerFilterAutoCompleteSelection
            label="TYPE"
            icon={<CardsIcon sx={{ fontSize: '20px' }} />}
            options={typesFilterOptions}
            groupBy={typesFilterGroupBy as TrackerFilterAutocompleteGroupByFunc}
            values={typeFilter}
            onChange={(values) => setTypeFilter(values as DropdownTableAutocompleteOptionType[])}
            type={eventHandler.type}
          />
          <TrackerFilterAutoCompleteSelection
            label="TEAM MEMBERS"
            icon={<UsersIcon sx={{ fontSize: '20px' }} />}
            options={teamMembersFilterOptions}
            groupBy={teamMembersFilterGroupBy as TrackerFilterAutocompleteGroupByFunc}
            values={teamMemberFilter}
            onChange={(values) => setTeamMemberFilter(values as DropdownTableAutocompleteOptionTeamMember[])}
            type={eventHandler.type}
          />
          <Grid xs={FILTER_INPUTS_GRID_COL_NUM}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Button
                sx={{ fontSize: '12px' }}
                onClick={onClose}
                variant="outlined"
                color={getColor()}
                data-cy="calendar-filter-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                sx={{ fontSize: '12px' }}
                variant="contained"
                color={getColor()}
                onClick={handleApply}
                data-cy="calendar-filter-save-btn"
              >
                Search
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Popover>
  );
}
